import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  OutlinedInput,
  Select,
  Button,
  MenuItem,
} from '@mui/material';
import { Save, Delete, Edit, Add, DeleteOutline } from '@mui/icons-material';

import './DynamicFieldsSection.css'; // import CSS file

const DynamicFieldsSection = ({
  title,
  data,
  setData,
  showNotification,
  fromService,
  fromDoors,
  categoryOptions
}) => {
  return (
    <Box className="dfs-container">
      <Typography variant="body1" className="dfs-title" style={{ fontWeight: 'bold' }}>
        {title}:
      </Typography>

      {data?.length > 0 ?
        data.map((item, index) => {
          const hasNewLabel = item.newLabel || item.newLabel === '';
          return (
            <Box key={index} className="dfs-row">
              {/* Label column */}
              <Box className="dfs-label">
                {item.default ? (
                  <Typography variant="body2">{item.label}</Typography>
                ) : (
                  <FormControl size="small" fullWidth>
                    <InputLabel>{item.newLabel}</InputLabel>
                    <OutlinedInput
                      label={item.newLabel}
                      type="text"
                      value={item.newLabel || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setData((prev) =>
                          prev.map((i, idx) =>
                            idx === index ? { ...i, newLabel: value } : i
                          )
                        );
                      }}
                    />
                  </FormControl>
                )}
              </Box>

              {/* Inputs + icons column */}
              <Box className="dfs-inputs">
                {hasNewLabel ? (
                  <>
                    {fromService && (
                      <FormControl size="small">
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={item.category || ''}
                          label="Category"
                          onChange={(e) => {
                            const value = e.target.value;
                            setData((prev) =>
                              prev.map((i, idx) =>
                                idx === index ? { ...i, category: value } : i
                              )
                            );
                          }}
                        >
                          {categoryOptions?.map((option, idx) => (
                            <MenuItem key={idx} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    <Save
                      className="dfs-icon"
                      color="primary"
                      onClick={() => {
                        if (item.newLabel !== '') {
                          setData((prev) =>
                            prev.map((i, idx) =>
                              idx === index
                                ? (() => {
                                  const { newLabel, ...rest } = i;
                                  return {
                                    ...rest,
                                    label: newLabel,
                                    default: true,
                                    editable: true,
                                  };
                                })()
                                : i
                            )
                          );
                        } else {
                          showNotification(
                            `Please provide value for ${title} label`,
                            'error'
                          );
                        }
                      }}
                    />
                    <Delete
                      className="dfs-icon"
                      color="secondary"
                      onClick={() =>
                        setData((prev) => prev.filter((_, idx) => idx !== index))
                      }
                    />
                  </>
                ) : (
                  <>
                    {/* Show category if exists */}
                    {item.category && (
                      <Typography className="dfs-category">
                        {item.category}
                      </Typography>
                    )}

                    {/* Qty input for fromService */}
                    {fromService && (
                      <FormControl size="small" className="dfs-small-input" style={{ width: fromService ? 100 : '100%' }}>
                        <InputLabel>Qty</InputLabel>
                        <OutlinedInput
                          label="Qty"
                          type="number"
                          value={item.qty || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setData((prev) =>
                              prev.map((i) =>
                                i.id === item.id ? { ...i, qty: value } : i
                              )
                            );
                          }}
                        />
                      </FormControl>
                    )}

                    {/* Remarks or Value input */}
                    <FormControl size="small" fullWidth style={{ width: fromService ? 450 : '100%' }}>
                      <InputLabel>{fromService ? 'Remarks' : item.label}</InputLabel>
                      <OutlinedInput
                        label={fromService ? 'Remarks' : item.label}
                        type="text"
                        value={item.value || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setData((prev) =>
                            prev.map((i) =>
                              i.id === item.id ? { ...i, value } : i
                            )
                          );
                        }}
                      />
                    </FormControl>

                    {/* Edit icon */}
                    {item.editable && (
                      <Edit
                        className="dfs-icon"
                        color="primary"
                        onClick={() =>
                          setData((prev) =>
                            prev.map((i, idx) =>
                              idx === index
                                ? {
                                  ...i,
                                  newLabel: i.label,
                                  default: false,
                                  editable: true,
                                }
                                : i
                            )
                          )
                        }
                      />
                    )}

                    {/* Delete icon */}
                    <Delete
                      className="dfs-icon"
                      color="secondary"
                      onClick={() =>
                        setData((prev) => prev.filter((_, idx) => idx !== index))
                      }
                    />
                  </>
                )}
              </Box>
            </Box>
          );
        }) : <Box>
          {Object.entries(data).map(([doorKey, doorItems]) => (
            <Box key={doorKey} style={{ border: '1px solid black', padding: 10, marginBottom: 5 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1" className="dfs-title" style={{ fontWeight: 'bold' }}>{doorKey}</Typography>
                <DeleteOutline
                  className="dfs-icon"
                  color="secondary"
                  onClick={() => {
                    setData((prev) => {
                      const { [doorKey]: _, ...remainingDoors } = prev; // Remove the door by its key
                      return remainingDoors;
                    });
                  }
                  }
                />
              </Box>
              {doorItems.map((item, index) => {
                const hasNewLabel = item.newLabel || item.newLabel === '';
                return (
                  <Box key={index} className="dfs-row">
                    {/* Label column */}
                    <Box className="dfs-label">
                      {item.default ? (
                        <Typography variant="body2">{item.label}</Typography>
                      ) : (
                        <FormControl size="small" fullWidth>
                          <InputLabel>{item.newLabel}</InputLabel>
                          <OutlinedInput
                            label={item.newLabel}
                            type="text"
                            value={item.newLabel || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setData((prev) => {
                                const updatedDoors = { ...prev };
                                updatedDoors[doorKey] = updatedDoors[doorKey].map((field) =>
                                  field.id === item.id ? { ...field, newLabel: value } : field
                                );
                                return updatedDoors;
                              });
                            }}
                          />
                        </FormControl>
                      )}
                    </Box>

                    {/* Inputs + icons column */}
                    <Box className="dfs-inputs">
                      {hasNewLabel ? (
                        <>
                          <Save
                            className="dfs-icon"
                            color="primary"
                            onClick={() => {
                              if (item.newLabel !== '') {
                                setData((prev) => {
                                  const updatedDoors = { ...prev };
                                  updatedDoors[doorKey] = updatedDoors[doorKey].map((field, idx) =>
                                    idx === index
                                      ? (() => {
                                        const { newLabel, ...rest } = field; // Destructure to extract newLabel
                                        return {
                                          ...rest, // Copy the rest of the properties
                                          label: newLabel, // Update the label with newLabel
                                          default: true, // Set default to true
                                          editable: true, // Set editable to true
                                        };
                                      })()
                                      : field
                                  );
                                  return updatedDoors;
                                });
                              } else {
                                showNotification(
                                  `Please provide value for ${title} label`,
                                  'error'
                                );
                              }
                            }}
                          />
                          <Delete
                            className="dfs-icon"
                            color="secondary"
                            onClick={() => {
                              setData((prev) => {
                                const updatedDoors = { ...prev };

                                // Iterate over all doors
                                for (let doorKey in updatedDoors) {
                                  updatedDoors[doorKey] = updatedDoors[doorKey].filter((field, idx) => idx !== index);
                                }

                                return updatedDoors;
                              });
                            }
                            }
                          />
                        </>
                      ) : (
                        <>
                          <FormControl size="small" fullWidth>
                            <InputLabel>{fromService ? 'Remarks' : item.label}</InputLabel>
                            <OutlinedInput
                              label={fromService ? 'Remarks' : item.label}
                              type="text"
                              value={item.value || ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                setData((prev) => {
                                  const updatedDoors = { ...prev };
                                  updatedDoors[doorKey] = updatedDoors[doorKey].map((field) =>
                                    field.id === item.id
                                      ? { ...field, value } // Update the field value for the matching ID
                                      : field
                                  );
                                  return updatedDoors;
                                });
                              }}
                            />
                          </FormControl>
                          {item.editable && (
                            <Edit
                              className="dfs-icon"
                              color="primary"
                              onClick={() => {
                                setData((prev) => {
                                  const updatedDoors = { ...prev };

                                  // Loop over each door
                                  for (let doorKey in updatedDoors) {
                                    updatedDoors[doorKey] = updatedDoors[doorKey].map((field, idx) =>
                                      idx === index // Compare the index for the field
                                        ? {
                                          ...field, // Copy the existing properties
                                          newLabel: field.label, // Set newLabel to current label
                                          default: false, // Set default to false
                                          editable: true, // Set editable to true
                                        }
                                        : field // Return the unchanged field if index doesn't match
                                    );
                                  }

                                  return updatedDoors;
                                });
                              }}
                            />
                          )}

                          {/* Delete icon */}
                          <Delete
                            className="dfs-icon"
                            color="secondary"
                            onClick={() => {
                              setData((prev) => {
                                const updatedDoors = { ...prev };

                                // Iterate over all doors
                                for (let doorKey in updatedDoors) {
                                  updatedDoors[doorKey] = updatedDoors[doorKey].filter((field, idx) => idx !== index);
                                }

                                return updatedDoors;
                              });
                            }}
                          />
                        </>
                      )}
                    </Box>
                  </Box>
                );
              })}
              <Button
                variant='contained'
                startIcon={<Add />}
                color='primary'
                size='small'
                onClick={() => {
                  const hasBlank = doorItems.some(
                    (i) => i.newLabel === '' && i.value === ''
                  );
                  if (!hasBlank) {
                    setData((prev) => {
                      const updatedDoors = { ...prev };
                      updatedDoors[doorKey] = [...updatedDoors[doorKey], { id: doorItems.length + 1, newLabel: '', value: '' }];
                      return updatedDoors;
                    });
                  }
                }}
                style={{ marginLeft: 10, marginBottom: 5 }}
              >
                Add Field
              </Button>
            </Box>
          ))}
        </Box>}
      {!fromDoors && (<Button
        variant='contained'
        startIcon={<Add />}
        color='primary'
        size='small'
        onClick={() => {
          const hasBlank = data.some(
            (i) => i.newLabel === '' && i.value === ''
          );
          if (!hasBlank) {
            setData([...data, { id: data.length + 1, newLabel: '', value: '' }]);
          }
        }}
        style={{ marginLeft: 10 }}
      >
        Add Field
      </Button>)}

      {fromDoors && (
        <>
          <br />
          <Button
            variant="contained"
            color="primary"
            size='small'
            startIcon={<Add />}
            style={{ marginLeft: 10 }}
            onClick={() => {
              const newDoorKey = `Door_${Object.keys(data).length + 1}`; // Generate a new door key
              setData((prev) => ({
                ...prev,
                [newDoorKey]: [
                  { id: 1, label: 'Door No', value: '', default: true },
                  { id: 2, label: 'Door Size', value: '', default: true },
                  { id: 3, label: 'Door Profile', value: '', default: true },
                  { id: 4, label: 'Door Material', value: '', default: true },
                  { id: 5, label: 'Frame Material', value: '', default: true }
                ]
              }));
            }} sx={{ mb: 2 }}>
            Add Section
          </Button>
        </>
      )}
    </Box>
  );
};

export default DynamicFieldsSection;
